import { useState } from 'react'
import { diffDays } from '../../shared/date'
import { CycleRing } from '../components/CycleRing'
import { LogSheet } from '../components/LogSheet'
import { Button, Card } from '../components/ui'
import { useI18n } from '../i18n'
import { useReady } from '../store'

const PHASE_COLOR: Record<string, string> = {
  menstrual: 'text-period',
  fertile: 'text-fertile',
  ovulation: 'text-ovulation',
  pms: 'text-pms',
  late: 'text-period',
  luteal: 'text-luteal',
  follicular: 'text-muted',
  unknown: 'text-muted',
}

export function Home() {
  const { t, formatDate } = useI18n()
  const { snapshot, prediction } = useReady()
  const [sheetOpen, setSheetOpen] = useState(false)
  const today = snapshot.today

  // 本周期内已记录的经期日，用来画环上的实心粉段
  const periodDaysThisCycle = prediction.currentCycleStart
    ? snapshot.periods
        .filter((p) => diffDays(prediction.currentCycleStart!, p.date) >= 0)
        .map((p) => p.date)
    : []

  const todayPeriod = snapshot.periods.find((p) => p.date === today)
  const todaySymptom = snapshot.symptoms.find((s) => s.date === today)
  const todayIntimacy = snapshot.intimacy.find((i) => i.date === today)
  const hasTodayLog = Boolean(todayPeriod || todaySymptom || todayIntimacy)

  return (
    <div className="px-4 pb-6 space-y-4">
      <header className="safe-top pb-1 px-1">
        <p className="text-sm text-muted">{snapshot.settings.name}</p>
        <h1 className="text-2xl font-semibold mt-0.5">
          {t.home.greeting(snapshot.user.displayName, formatDate(today))}
        </h1>
      </header>

      {!prediction.hasData ? (
        <EmptyState onLog={() => setSheetOpen(true)} />
      ) : (
        <>
          <div className="py-2">
            <CycleRing prediction={prediction} periodDays={periodDaysThisCycle}>
              {/* 环里的信息层级：现在处于什么阶段 → 第几天 → 还有多久来 */}
              <div
                role="status"
                aria-label={t.home.phaseAria(
                  t.phase[prediction.phase],
                  t.home.cycleDay(prediction.cycleDay ?? 0),
                )}
              >
                <p className={`text-sm font-medium ${PHASE_COLOR[prediction.phase]}`}>
                  {t.phase[prediction.phase]}
                </p>
                <p className="text-[42px] leading-none font-semibold mt-1.5 tabular-nums">
                  {prediction.cycleDay}
                  <span className="text-base font-normal text-muted ml-1">
                    {t.home.dayUnit(prediction.cycleDay ?? 0)}
                  </span>
                </p>
                <p className="text-[13px] mt-2.5 leading-snug text-ink/75">
                  {prediction.lateBy
                    ? t.home.lateBy(prediction.lateBy)
                    : t.home.untilPeriod(prediction.daysUntilPeriod ?? 0)}
                </p>
              </div>
            </CycleRing>
          </div>

          <Card>
            <p className="text-[15px] leading-relaxed">{t.phaseHint[prediction.phase]}</p>
          </Card>

          <div className="grid grid-cols-2 gap-3">
            <MiniCard
              tone="period"
              label={t.calendar.legendPredicted}
              value={formatDate(prediction.nextPeriod!.expected)}
              hint={t.home.expectedRange(
                formatDate(prediction.nextPeriod!.earliest),
                formatDate(prediction.nextPeriod!.latest),
              )}
            />
            <MiniCard
              tone="fertile"
              label={t.calendar.legendFertile}
              value={t.home.fertileFrom(formatDate(prediction.fertileWindow!.start))}
              hint={t.home.ovulationOn(formatDate(prediction.ovulation!.expected))}
            />
          </div>

          {hasTodayLog && (
            <Card>
              <p className="text-sm text-muted mb-2">{t.home.todaySummary}</p>
              <div className="flex flex-wrap gap-2">
                {todayPeriod && <Tag tone="period">{t.flow[todayPeriod.flow]}</Tag>}
                {todaySymptom?.symptoms.map((s) => (
                  <Tag key={s}>{t.symptom[s as keyof typeof t.symptom] ?? s}</Tag>
                ))}
                {todayIntimacy && (
                  <Tag tone="fertile">{t.protection[todayIntimacy.protection]}</Tag>
                )}
              </div>
            </Card>
          )}

          <Button className="w-full" onClick={() => setSheetOpen(true)}>
            {hasTodayLog ? t.home.editToday : t.home.logToday}
          </Button>
        </>
      )}

      <LogSheet date={today} open={sheetOpen} onClose={() => setSheetOpen(false)} />
    </div>
  )
}

function EmptyState({ onLog }: { onLog: () => void }) {
  const { t } = useI18n()
  return (
    <Card className="text-center py-10">
      <img
        src="/icons/icon.svg"
        alt=""
        width="56"
        height="56"
        className="w-14 h-14 rounded-2xl mx-auto mb-4"
      />
      <p className="font-medium">{t.home.noData}</p>
      <p className="text-sm text-muted mt-2 mb-6 leading-relaxed px-4">
        {t.home.noDataHint}
      </p>
      <Button onClick={onLog}>{t.home.startPeriod}</Button>
    </Card>
  )
}

function MiniCard({
  tone,
  label,
  value,
  hint,
}: {
  tone: 'period' | 'fertile'
  label: string
  value: string
  hint: string
}) {
  const dot = tone === 'period' ? 'bg-period' : 'bg-fertile'
  return (
    <div className="rounded-3xl bg-card border border-line/70 p-4">
      <div className="flex items-center gap-1.5 mb-2">
        <span className={`w-2 h-2 rounded-full ${dot}`} />
        <span className="text-xs text-muted">{label}</span>
      </div>
      <p className="text-[17px] font-semibold">{value}</p>
      <p className="text-[11px] text-muted mt-1.5 leading-snug">{hint}</p>
    </div>
  )
}

function Tag({
  children,
  tone = 'default',
}: {
  children: React.ReactNode
  tone?: 'default' | 'period' | 'fertile'
}) {
  const styles = {
    default: 'bg-bg-soft text-ink/80',
    period: 'bg-period-soft text-period',
    fertile: 'bg-fertile-soft text-fertile',
  }[tone]
  return <span className={`px-3 py-1.5 rounded-full text-xs ${styles}`}>{children}</span>
}
